// Human-readable map of the backend permission catalog.
//
// The backend catalog (constants/permissionCatalog.js) is a flat dictionary of
// ~60 raw module keys. That is fine for enforcement but unusable for authoring:
// searching "leads" in a flat key list never matches `leadManagement`, so the
// modules that gate "Manage Leads", "Lead Analytics" etc. look like they are
// missing from the editor.
//
// This file adds the presentation layer only — grouping, labels and a hint that
// names the exact sidebar entry / route each module unlocks — so an admin can
// say "show My Leads, hide Manage Leads" by reading the screen instead of
// guessing key names. It grants nothing on its own; the backend catalog stays
// the source of truth for which modules and actions exist.
//
// Groups mirror components/Layout/Sidebar.jsx. Any catalog module missing here
// still shows up, under "Other modules" — new backend modules never disappear.

export const MODULE_GROUPS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    modules: [
      { key: 'dashboard', label: 'Dashboard', hint: 'Sidebar → Dashboard / MyDashBoard' },
    ],
  },
  {
    key: 'leads',
    label: 'Leads / CSR',
    modules: [
      { key: 'myLeads', label: 'My Leads', hint: "Sidebar → Leads / CSR → My Leads (/leads). The rep's own queue and lead list." },
      { key: 'leadManagement', label: 'Manage Leads', hint: 'Sidebar → Leads / CSR → Manage Leads (/leads/manage). All-leads view + assignment. Leave off to hide it.' },
      { key: 'importLeads', label: 'Import Leads', hint: 'Sidebar → Leads / CSR → Import Leads (/leads/import)' },
      { key: 'leadAnalytics', label: 'Lead Analytics', hint: 'Sidebar → Leads / CSR → Lead Analytics (/leads/analytics)' },
      { key: 'leadTypes', label: 'Lead Types', hint: 'Sidebar → Leads / CSR → Lead Types (master data)' },
      { key: 'leadSources', label: 'Lead Sources', hint: 'Sidebar → Leads / CSR → Lead Sources (master data)' },
      { key: 'leadStatuses', label: 'Lead Statuses', hint: 'Sidebar → Leads / CSR → Lead Statuses (master data)' },
    ],
  },
  {
    key: 'calling',
    label: 'Calling',
    modules: [
      { key: 'callSync', label: 'Call Sync', hint: 'Dial a customer from the CRM on the rep\'s own phone. "initiate" is what actually places a call.' },
      { key: 'callHistory', label: 'Call History', hint: 'Call log on the lead / customer record' },
      { key: 'callAnalytics', label: 'Calling Dashboard', hint: 'Sidebar → Calling → Calling Dashboard' },
      { key: 'callDevices', label: 'Call Devices', hint: 'Sidebar → Calling → Call Devices (paired handsets)' },
      { key: 'callTargets', label: 'Calling Targets', hint: 'Sidebar → Calling → Calling Targets' },
    ],
  },
  {
    key: 'masters',
    label: 'Customers & Masters',
    modules: [
      { key: 'customer', label: 'Customers', hint: 'Sidebar → Customers (/customers), incl. KYC fields' },
      { key: 'branches', label: 'Branches', hint: 'Sidebar → Branches (/branches)' },
      { key: 'agents', label: 'Agents', hint: 'Sidebar → Agents + agent settlements' },
      { key: 'scheme', label: 'Schemes', hint: 'Scheme master (sidebar entry currently disabled)' },
      { key: 'billing', label: 'Billing', hint: 'Billing module (sidebar entry currently disabled)' },
    ],
  },
  {
    key: 'cases',
    label: 'Cases',
    modules: [
      { key: 'walkInCase', label: 'Walk-In Cases', hint: 'Sidebar → Walk-In Cases (/walk-in-cases)' },
      { key: 'pledgeReleaseCase', label: 'Pledge Release', hint: 'Sidebar → Pledge Release (/pledge-release-cases)' },
    ],
  },
  {
    key: 'inventory',
    label: 'Gold Vault & Inventory',
    modules: [
      { key: 'goldInventory', label: 'Inventory', hint: 'Sidebar → Inventory (/gold-inventory)' },
      { key: 'inventoryPurchaseLedger', label: 'Purchase Ledger', hint: 'Gold Vault → Purchase Ledger' },
      { key: 'inventoryBranchVault', label: 'Branch Vault', hint: 'Gold Vault → Branch Vault. Also gates the whole Gold Vault menu.' },
      { key: 'inventoryHoVault', label: 'HO Vault', hint: 'Gold Vault → HO Vault' },
      { key: 'inventoryTransfer', label: 'Transfers', hint: 'Gold Vault → Transfers' },
      { key: 'inventoryTransferApproval', label: 'Transfer Approvals', hint: 'Gold Vault → Transfer Approvals' },
      { key: 'inventoryTransferReceipt', label: 'Transfer Receipts', hint: 'Gold Vault → Transfer Receipts' },
      { key: 'inventoryBatch', label: 'Batches', hint: 'Melt batches (HO)' },
      { key: 'inventoryDispatch', label: 'Dispatches', hint: 'Dispatch to smelter' },
      { key: 'inventoryReceipt', label: 'Receipts', hint: 'Smelter receipt confirmation' },
      { key: 'inventoryProfit', label: 'Inventory Profit', hint: 'Profit figures on inventory screens' },
      { key: 'inventoryAudit', label: 'Audit Log', hint: 'Gold Vault → Audit Log' },
      { key: 'inventoryReports', label: 'Vault Reports', hint: 'Gold Vault → Reconciliation, Aging Report, Explorer' },
    ],
  },
  {
    key: 'purity',
    label: 'Purity Analyzer',
    modules: [
      { key: 'purityAnalyzer', label: 'Purity Analyzer (whole module)', hint: 'Legacy all-in-one gate: its actions still open every lab page. Prefer the per-page modules below — grant this one only for full lab access.' },
      { key: 'purityDashboard', label: 'Lab Dashboard', hint: 'Purity Analyzer → Lab Dashboard (/purity)' },
      { key: 'purityAssay', label: 'Assaying', hint: 'Purity Analyzer → Assaying (/purity/run). "create" runs a test, "edit" annotates, "reverse" reverses one.' },
      { key: 'purityTests', label: 'Test History', hint: 'Purity Analyzer → Test History (/purity/tests) + individual test results' },
      { key: 'purityCalibrate', label: 'Calibrate', hint: 'Purity Analyzer → Calibrate (/purity/calibrate). "create" records a copper calibration.' },
      { key: 'purityCalibrations', label: 'Calibration History', hint: 'Purity Analyzer → Calibration History (/purity/calibrations). "archive" retires a calibration.' },
      { key: 'purityXrf', label: 'XRF & Learning', hint: 'Purity Analyzer → XRF & Learning (/purity/xrf). "verify" records an XRF check, "propose" queues learning, "approve" accepts it.' },
      { key: 'purityVersions', label: 'Dataset Versions', hint: 'Purity Analyzer → Dataset Versions (/purity/versions). "activate" switches or rolls back the live calibration.' },
      { key: 'purityAnalytics', label: 'Lab Analytics', hint: 'Purity Analyzer → Analytics (/purity/analytics)' },
      { key: 'purityMachines', label: 'Machines', hint: 'Purity Analyzer → Machines (/purity/machines). Machine profile CRUD.' },
      { key: 'puritySettings', label: 'Lab Configure', hint: 'Purity Analyzer → Configure (/purity/settings). HM / Non-HM deduction percentages.' },
    ],
  },
  {
    key: 'smelter',
    label: 'Smelters',
    modules: [
      { key: 'smelter', label: 'Smelters', hint: 'Sidebar → Smelters (directory, dispatches, invoices, transit memos, GST recovery)' },
    ],
  },
  {
    key: 'finance',
    label: 'Accounting & Finance',
    modules: [
      { key: 'financials', label: 'Financials', hint: 'Sidebar → Financials (/financials)' },
      { key: 'accounting', label: 'Accounting', hint: 'Sidebar → Accounting (/accounting), incl. manual journal' },
    ],
  },
  {
    key: 'approvals',
    label: 'Approvals / Deviation',
    modules: [
      { key: 'approval', label: 'Approvals', hint: 'Sidebar → Deviation → Approvals / Pending' },
      { key: 'workflow', label: 'Workflow Rules', hint: 'Sidebar → Deviation → Rules. "myApprovals" gates the personal inbox.' },
    ],
  },
  {
    key: 'treasury',
    label: 'Treasury (IBCT)',
    modules: [
      { key: 'treasury', label: 'Treasury', hint: 'Gates the whole IBCT menu + Cash Transfers, Day Status' },
      { key: 'treasuryHoDashboard', label: 'HO Dashboard', hint: 'IBCT → HO Dashboard, All Transfers' },
      { key: 'treasuryBranchFund', label: 'Branch Fund', hint: 'IBCT → Branch Fund, Branch Expenses' },
      { key: 'treasuryCashLedger', label: 'Cash Ledger', hint: 'IBCT → Cash Ledger' },
      { key: 'treasuryCentralFund', label: 'Central Fund', hint: 'IBCT → Central Fund, Funding History' },
      { key: 'treasuryDayClosure', label: 'Day Closure', hint: 'IBCT → EOD Closure, Closure Approvals' },
      { key: 'treasuryApprovals', label: 'Payment Approvals', hint: 'IBCT → Payment Requests + Deviation → Payment Approvals' },
    ],
  },
  {
    key: 'hr',
    label: 'HR & Administration',
    modules: [
      { key: 'hr', label: 'HR (menu access)', hint: 'Gates the whole HR menu + Leave and Holiday Calendar' },
      { key: 'hrDashboard', label: 'HR Dashboard', hint: 'HR → HR Dashboard, Reports' },
      { key: 'hrEmployees', label: 'Employees', hint: 'HR → Employees (employee master, salary/bank fields below)' },
      { key: 'hrAttendance', label: 'Attendance', hint: 'HR → Attendance. "checkIn" is self check-in only.' },
      { key: 'hrLeave', label: 'Leave', hint: 'Leave requests. "approve" gates approving others.' },
      { key: 'hrHoliday', label: 'Holiday Calendar', hint: 'Holiday master + branch closure application' },
      { key: 'hrAssets', label: 'Assets', hint: 'HR → Assets (issue / return company assets)' },
      { key: 'hrDocuments', label: 'Documents', hint: 'HR → Documents' },
      { key: 'hrPerformance', label: 'Performance', hint: 'HR → Performance' },
      { key: 'hrPayroll', label: 'Payroll', hint: 'HR → Payroll Prep, payslips' },
      { key: 'hrExit', label: 'Exit', hint: 'HR → Exit / full & final' },
    ],
  },
  {
    key: 'admin',
    label: 'Administration & Security',
    modules: [
      { key: 'user', label: 'Users', hint: 'User list and user records' },
      { key: 'permission', label: 'Permission Sets', hint: 'Sidebar → Permission Set. "edit" also unlocks Dashboard Builder. Grant with care.' },
      { key: 'security', label: 'Security', hint: 'Sidebar → Security (login policies, devices, audit)' },
      { key: 'system', label: 'System Settings', hint: 'Sidebar → Settings. Each sub-page is a FIELD below (backup, generalSettings, apiConfig, karatDeductions).' },
      { key: 'smm', label: 'SMM', hint: 'Social media module (sidebar entry currently disabled)' },
    ],
  },
];

// module key -> { label, hint, groupKey, groupLabel }
export const MODULE_META = MODULE_GROUPS.reduce((acc, group) => {
  group.modules.forEach((m) => {
    acc[m.key] = { ...m, groupKey: group.key, groupLabel: group.label };
  });
  return acc;
}, {});

export const moduleLabel = (key) => MODULE_META[key]?.label || key;

// Friendly names for the actions that repeat across modules. Anything not
// listed falls back to a camelCase -> "Title Case" split.
const ACTION_LABELS = {
  all: 'All (wildcard)',
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  assign: 'Assign',
  import: 'Import',
  export: 'Export',
  initiate: 'Initiate',
  cancel: 'Cancel',
  checkIn: 'Check in',
  applyClosure: 'Apply closure',
  myApprovals: 'My approvals inbox',
  rules: 'Manage rules',
};

export const humanize = (key = '') =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

export const actionLabel = (action) => ACTION_LABELS[action] || humanize(action);

export const fieldLabel = (field) => (field === 'all' ? 'All (wildcard)' : humanize(field));

// Order the catalog into groups, appending anything the map doesn't know about
// so a newly added backend module is still configurable.
export const buildGroupedModules = (modules = []) => {
  const known = new Set();
  const groups = MODULE_GROUPS.map((group) => {
    const items = group.modules
      .filter((m) => modules.includes(m.key))
      .map((m) => {
        known.add(m.key);
        return m;
      });
    return { ...group, modules: items };
  }).filter((g) => g.modules.length);

  const leftovers = modules.filter((m) => !known.has(m));
  if (leftovers.length) {
    groups.push({
      key: '__other',
      label: 'Other modules',
      modules: leftovers.map((key) => ({ key, label: humanize(key), hint: '' })),
    });
  }
  return groups;
};
